import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import SignaturePad from '@/components/SignaturePad';
import LeverFotosPanel from '@/components/LeverFotosPanel';
import { fmt } from '@/lib/api';

export default function LeverbonTekenenPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const API = process.env.REACT_APP_BACKEND_URL;

  const load = () => {
    axios.get(`${API}/api/public/leverbon-tekenen/${token}`)
      .then(r => { setData(r.data); setErr(''); if (r.data.doc.handtekening_datum) setSaved(true); })
      .catch(e => setErr(e.response?.data?.detail || e.response?.status === 410 ? 'Deze teken-link is verlopen.' : 'Link niet gevonden.'));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  const tekenen = async ({ dataUrl, naam }) => {
    setBusy(true);
    try {
      await axios.post(`${API}/api/public/leverbon-tekenen/${token}`, { handtekening: dataUrl, naam });
      setSaved(true);
    } catch (e) {
      alert(e.response?.data?.detail || 'Opslaan mislukt');
    }
    setBusy(false);
  };

  if (err) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#F3F4F6', padding: 16 }}>
        <div className="max-w-md text-center bg-white p-6 rounded-lg border border-gray-200">
          <div className="text-4xl mb-3">⚠</div>
          <div className="text-lg font-bold mb-1">{err}</div>
          <div className="text-xs text-gray-500">Neem contact op met de leverancier voor een nieuwe link.</div>
        </div>
      </div>
    );
  }

  if (!data) {
    return <div className="min-h-screen flex items-center justify-center text-gray-400">Laden…</div>;
  }

  const { doc, klant, bedrijf } = data;

  return (
    <div className="min-h-screen" style={{ background: '#F3F4F6' }}>
      <div className="max-w-3xl mx-auto p-4 sm:p-6 space-y-4">
        {/* Bedrijfsheader */}
        <div className="bg-white rounded-lg border border-gray-200 p-4 flex items-center gap-3">
          {bedrijf.logo && <img src={bedrijf.logo} alt="logo" className="h-12 w-auto object-contain" />}
          <div className="flex-1 min-w-0">
            <div className="font-bold text-lg truncate">{bedrijf.naam || 'Leveringsbon'}</div>
            {bedrijf.adres && <div className="text-xs text-gray-500 truncate">{bedrijf.adres}</div>}
          </div>
        </div>

        {/* Doc info */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 bg-[#059669] text-white">
            <div className="text-xs uppercase tracking-wider opacity-90">Leveringsbon</div>
            <div className="text-xl font-bold font-mono">{doc.id}</div>
          </div>
          <div className="p-4 space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-gray-500">Datum</span><span className="font-medium">{doc.datum}</span></div>
            {klant.naam && <div className="flex justify-between"><span className="text-gray-500">Klant</span><span className="font-medium">{klant.naam}</span></div>}
            {doc.ref && <div className="flex justify-between"><span className="text-gray-500">Referentie</span><span className="font-medium">{doc.ref}</span></div>}
          </div>

          {/* Lijnen */}
          <div className="border-t border-gray-100">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
                <tr><th className="text-left p-2">Omschrijving</th><th className="text-right p-2 w-16">Aantal</th><th className="text-right p-2 w-20 hidden sm:table-cell">Eenheid</th></tr>
              </thead>
              <tbody>
                {(doc.lijnen || []).map((l, i) => (
                  <tr key={i} className="border-t border-gray-100">
                    <td className="p-2">{l.omschr}</td>
                    <td className="p-2 text-right font-mono">{l.aantal}</td>
                    <td className="p-2 text-right text-gray-500 hidden sm:table-cell">{l.eenheid || 'st'}</td>
                  </tr>
                ))}
                {(doc.lijnen || []).length === 0 && <tr><td colSpan="3" className="p-4 text-center text-gray-400 text-xs">Geen regels</td></tr>}
              </tbody>
            </table>
          </div>

          {doc.nota && <div className="p-4 border-t border-gray-100 text-xs text-gray-600 whitespace-pre-wrap">{doc.nota}</div>}
        </div>

        {/* Foto's bij levering (schade/bewijs) */}
        {!saved && (
          <div className="bg-white rounded-lg border border-gray-200 p-4" data-testid="lever-fotos-sectie-public">
            <div className="font-bold text-sm mb-1">📷 Foto's bij levering</div>
            <div className="text-xs text-gray-500 mb-3">Is er schade of wilt u de staat van de goederen vastleggen? Maak hier één of meerdere foto's vóór u ondertekent.</div>
            <LeverFotosPanel
              listFotos={() => axios.get(`${API}/api/public/leverbon-tekenen/${token}/fotos`).then(r => r.data)}
              uploadFoto={(file, opm) => { const fd = new FormData(); fd.append('file', file); fd.append('opmerking', opm || ''); return axios.post(`${API}/api/public/leverbon-tekenen/${token}/foto`, fd); }}
              deleteFoto={(fid) => axios.delete(`${API}/api/public/leverbon-tekenen/${token}/foto/${fid}`)}
              updateOpmerking={(fid, opm) => axios.put(`${API}/api/public/leverbon-tekenen/${token}/foto/${fid}`, { opmerking: opm })}
            />
          </div>
        )}

        {/* Handtekening */}
        {saved ? (
          <div className="bg-green-50 border border-green-300 rounded-lg p-6 text-center" data-testid="lev-signed">
            <div className="text-5xl mb-2">✓</div>
            <div className="text-xl font-bold text-green-800 mb-1">Bedankt!</div>
            <div className="text-sm text-green-700 mb-4">Uw handtekening is ontvangen.</div>
            {doc.handtekening_datum && <div className="text-xs text-green-600 mb-3">Ondertekend op {new Date(doc.handtekening_datum).toLocaleString('nl-BE')}</div>}
            <div className="text-[11px] text-gray-500 mt-4">
              Deze bevestiging is ook als PDF met uw handtekening naar {bedrijf.email || 'de leverancier'} verstuurd.<br />
              U kan dit venster sluiten.
            </div>
          </div>
        ) : (
          <SignaturePad
            width={700}
            height={200}
            initialName={klant.naam || ''}
            onSave={tekenen}
          />
        )}

        <div className="text-center text-[10px] text-gray-400 py-2">
          Deze link kan maar 1× gebruikt worden en vervalt automatisch na ondertekening.{busy && ' · Bezig met opslaan…'}
        </div>
      </div>
    </div>
  );
}
